import {
  Button,
  DefaultAppLanding,
  DefaultLayout,
  PageContentWrapper,
  PageHeaderImage,
  useArtistContext,
} from "Components";
import { FlexColumn, FlexRow } from "Components/Flex";
import { Body1, H2, H3, H4, Li } from "melodies-source/Text";
import { GlobalStyles } from "styled";
import styled from "styled-components";
import { Card } from "melodies-source/Card";
import { TextInput } from "melodies-source/TextInput";
import { SvgSearch } from "melodies-source/Svgs/Search";
import { useEffect, useState } from "react";
import DropdownMenu from "Components/DropdownMenu";
import { SvgDropdown } from "melodies-source/Svgs/Dropdown";
import { SvgClipboard } from "melodies-source/Svgs/Clipboard";
import { useArtistSurveys } from "hooks/useArtistSurveys";
import { SurveyStatus } from "@max/common/dist/setfan";
import "react-loading-skeleton/dist/skeleton.css";
import { useHistory } from "react-router-dom";
import { useScrollLockContext } from "contexts/ScrollLockContext";
import {
  ConfirmFeatureModal,
  GetFeaturedLinkModal,
} from "./Components/GetLinkModal";
import { ReactComponent as SvgStar } from "./Components/SvgStar.svg";
import { getPublishFunction } from "@max/common/dist/functions/setfan/publish";
import { getFunctions } from "firebase/functions";
import { addToast } from "melodies-source/Toast";
import { Link } from "melodies-source/Link";
import {
  query,
  collection,
  getFirestore,
  where,
  updateDoc,
} from "firebase/firestore";
import { useCollection } from "react-firebase-hooks/firestore";
import { TemplateProvider, useTemplates } from "./Components/Templates";
import { SvgAddAlt } from "melodies-source/Svgs/AddAlt";
import { SetFanKnockout } from "assets/logos";
import animationData from "assets/lotties/fan-marketing-animation.json";
import { IconBroadCast, IconGearAlt, IconPalette, IconPulse } from "assets/svg";
import { StyledPagination } from "Components/Pagination";
import { useMobileMediaQuery, useTabletMediaQuery } from "hooks";
import { PublishModal } from "../Components/PublishModal";
import toast from "react-hot-toast";
import {
  SurveyCard,
  SurveyCardLoader,
  SurveyMenuOption,
} from "./Components/SurveyCard";
import { Typesense } from "@max/common/typesense";
import { EditAdditionalGroups } from "./Components/EditAdditionalGroups";
import { ConfirmLiveEdit } from "./Components/ConfirmLiveEdit";

type SurveyTypesenseDocument = Typesense<"surveys">;

const publish = getPublishFunction(getFunctions());

const SortByOptions = [
  "Newest",
  "Oldest",
  "Published",
  "Draft",
  "Ended",
] as const;
type SortBy = (typeof SortByOptions)[number];

export const Dashboard = () => {
  return (
    <TemplateProvider>
      <DashboardInternal />
    </TemplateProvider>
  );
};

const DashboardInternal = () => {
  const isMobile = useMobileMediaQuery();
  const isTablet = useTabletMediaQuery();
  const history = useHistory();

  const { id } = useArtistContext();
  const { setModal } = useTemplates();

  useEffect(() => {
    if (history.location.state && history.location.state["create"] === true) {
      setModal({ selectCategory: true });
    }
  }, []);

  const { lockScroll } = useScrollLockContext();

  const CardHeader = isMobile ? H3 : H2;

  const [search, setSearch] = useState("");
  const [sortBy, setSortBy] = useState<SortBy>(SortByOptions[0]);
  const [surveyUpdating, setSurveyUpdating] = useState("");
  const [featuredLinkOpen, setFeaturedLinkOpen] = useState(false);
  const [surveyToFeature, setSurveyToFeature] =
    useState<SurveyTypesenseDocument | null>(null);
  const [subdomains] = useCollection(
    query(
      collection(getFirestore(), `artist_subdomain`),
      where("artistGroupId", "==", id),
    ),
  );
  const [surveyToPublish, setSurveyToPublish] =
    useState<SurveyTypesenseDocument | null>(null);

  // Confirmation modal state for editing live surveys
  const [confirmLiveEditOpen, setConfirmLiveEditOpen] = useState(false);
  const [pendingMenuAction, setPendingMenuAction] = useState<{
    menuItem: SurveyMenuOption;
    survey: SurveyTypesenseDocument;
  } | null>(null);

  const subdomainDoc = subdomains?.docs?.[0];

  const hasFeaturedSupport = subdomainDoc?.exists();

  const featuredId = subdomainDoc?.data()?.surveyId;

  const surveyFilterBy = () => {
    switch (sortBy) {
      case "Draft":
        return "status:=[draft,unpublished]";
      case "Ended":
        return "status:=[completed,cancelled]";
      case "Published":
        return "status:=[published,live]";
      // sort by handled below
      case "Newest":
      case "Oldest":
        break;
      default:
        throw new Error(`invalid sortBy selection: ${sortBy}`);
    }
  };

  const filterBy = [
    surveyFilterBy(),
    "surveyType:=1",
    `(artistGroupId:${id} || additionalGroups:=[${id}])`,
    `status:!=deleted`,
  ].filter((v) => !!v);

  const [currentPage, setCurrentPage] = useState(1);
  const {
    documents: surveys,
    loading: loadingSurveys,
    forceReload,
    totalDocuments,
    totalArtistCampaigns,
  } = useArtistSurveys({
    sortBy: sortBy === "Oldest" ? "createdAt:asc" : "createdAt:desc",
    filterBy: filterBy.join(" && "),
    groupId: id,
    query: search,
    queryBy: "title,status,description",
    page: currentPage,
  });

  // when search is used, set the results page to 1, otherwise
  // you might try to get a page that does not exist
  useEffect(() => {
    setCurrentPage(1);
  }, [search]);

  const updateStatus = async (
    surveyId: string,
    status: Exclude<SurveyStatus, "unpublished" | "published" | "cancelled">,
  ) => {
    setSurveyUpdating(surveyId);
    try {
      const {
        data: { success },
      } = await publish({ surveyId, status });

      if (!success) {
        throw new Error("publish returned unsuccessfully");
      } else {
        forceReload();
      }
    } catch (err) {
      console.error(err);
      addToast(
        `Unable to ${
          status === "live"
            ? "resume"
            : status === "completed"
            ? "end"
            : "delete"
        } campaign. Please try again.`,
        "error",
      );
    } finally {
      setSurveyUpdating("");
    }
  };

  const currentTitle = surveys.find(
    (tSurvey) => tSurvey.surveyId === featuredId,
  )?.title;

  const updateFeatured = () => {
    updateDoc(subdomainDoc.ref, {
      surveyId: surveyToFeature.surveyId,
    })
      .then(() => {
        addToast(
          `${subdomainDoc.id}.set.fan now points to ${surveyToFeature.title}`,
          "success",
        );
      })
      .catch(() => {
        addToast(`Updating featured event failed.`, "error");
      });
    setSurveyToFeature(null);
  };

  // this controls edit modal
  const [editAdditionalGroups, setEditAdditionalGroups] = useState("");

  const onSurveyMenuSelect = async (
    menuItem: SurveyMenuOption,
    survey: SurveyTypesenseDocument,
    options?: { bypassConfirm?: boolean },
  ) => {
    // If editing a live survey, show confirmation modal unless bypassed
    if (
      menuItem === "editSurvey" &&
      survey.status === "live" &&
      !options?.bypassConfirm
    ) {
      setPendingMenuAction({ menuItem, survey });
      setConfirmLiveEditOpen(true);
      return;
    }
    const surveyId = survey.surveyId;
    switch (menuItem) {
      case "fanView":
        window.open(`${process.env.REACT_APP_SET_FAN_URL}/${surveyId}`);
        break;
      case "addGroup":
        setEditAdditionalGroups(surveyId);
        break;
      case "editSurvey":
        history.push(`surveys/${surveyId}`);
        break;
      case "endSurvey":
        await updateStatus(surveyId, "completed");
        break;
      case "resumeSurvey":
        await updateStatus(surveyId, "live");
        break;
      case "deleteSurvey":
        await updateStatus(surveyId, "deleted");
        break;
      case "setFeatured":
        setSurveyToFeature(survey);
        break;
      default:
        throw new Error(`invalid menu selection for survey: ${menuItem}`);
    }
  };

  const handlePublish = () => {
    try {
      updateStatus(surveyToPublish?.id, "live");
      setSurveyToPublish(null);
      toast.success("Survey published successfully");
    } catch (err) {
      console.error(err);
      toast.error("Unable to publish survey. Please try again.");
    }
  };

  const handlePublishSubdomain = async () => {
    try {
      await updateDoc(subdomains.docs[0].ref, {
        surveyId: surveyToPublish?.id,
      });
      handlePublish();
    } catch (err) {
      console.error(err);
      toast.error("Unable to publish survey. Please try again.");
    }
  };

  return (
    <DefaultLayout>
      <GlobalStyles grayBG />
      <PageHeaderImage app="fan" />
      <PageContentWrapper
        title={
          <SetFanKnockout
            style={{
              ...(!loadingSurveys &&
                !totalArtistCampaigns && { alignSelf: "center" }),
            }}
          />
        }
        action={
          !!totalArtistCampaigns && (
            <CreateSurveyButton
              variant="primary"
              leftIcon={<SvgAddAlt />}
              onClick={() => {
                lockScroll();
                setModal({ selectCategory: true });
              }}
              style={{
                backgroundColor: "var(--set-fan-primary)",
                color: "var(--main-color)",
              }}
            >
              Create Campaign
            </CreateSurveyButton>
          )
        }
      >
        {!loadingSurveys && !totalArtistCampaigns ? (
          <DefaultAppLanding
            app="fan"
            title="Turn Followers Into Contacts"
            body={
              <FlexColumn gap="20px">
                <Body1>
                  SET.Fan empowers artists to collect fan contact info and
                  insights via custom sign-up forms, campaigns, sweepstakes and
                  more.
                </Body1>
                <BulletList $iconColor="var(--set-fan-primary)">
                  <IconGearAlt />
                  <Li>Reclaim control from social media algorithms</Li>
                  <IconBroadCast />
                  <Li>
                    Easily promotable on Instagram, TikTok and livestreams
                  </Li>
                  <IconPulse />
                  <Li>Gather insights to power your artist marketing</Li>
                  <IconPalette />
                  <Li>Create a sweepstakes or campaign in 2 minutes</Li>
                </BulletList>
              </FlexColumn>
            }
            actionText="Get Started"
            onAction={() => {
              lockScroll();
              setModal({ selectCategory: true });
            }}
            video={{
              url: "https://storage.googleapis.com/set-live-stage.appspot.com/artist-portal/video/set-fan-promotion.mp4",
            }}
            animation={animationData}
          />
        ) : (
          <SurveysCard isElevated>
            <CardHeader
              style={{ color: "var(--main-color)", marginBottom: 28 }}
            >
              Campaigns
            </CardHeader>
            <CardActions>
              <SearchInput
                leftIcon={<SvgSearch />}
                value={search}
                placeholder="Search for a campaign"
                onChange={setSearch}
              />
              <div>
                {featuredId && (
                  <Link
                    style={{
                      display: "flex",
                      alignItems: "center",
                      marginRight: "20px",
                    }}
                    onClick={() => setFeaturedLinkOpen(true)}
                  >
                    <SvgStar style={{ marginRight: "5px" }} />
                    Get Featured Link
                  </Link>
                )}
                <DropdownMenu
                  labelAs={H4}
                  options={SortByOptions.map((opt) => ({
                    label: opt,
                    value: opt,
                  }))}
                  icon={<SvgDropdown />}
                  onClick={(val) => setSortBy(val)}
                  value={sortBy}
                  forceSide={isMobile && !isTablet ? "right" : "left"}
                />
              </div>
            </CardActions>
            <SurveyList>
              {loadingSurveys ? (
                <SurveyCardLoader />
              ) : surveys.length ? (
                surveys.map((survey) =>
                  surveyUpdating === survey.id ? (
                    <SurveyCardLoader key={survey.id} />
                  ) : (
                    <SurveyCard
                      key={survey.id}
                      survey={survey}
                      onSurveyMenuSelect={onSurveyMenuSelect}
                      hasFeaturedSupport={hasFeaturedSupport}
                      featuredId={featuredId}
                      featuredUrl={`https://${subdomainDoc?.id}.set.fan`}
                      onPublish={setSurveyToPublish}
                    />
                  ),
                )
              ) : (
                <NoSurveyContainer>
                  <SvgClipboard />
                  <H3>
                    There are no campaigns matching filter{" "}
                    <strong>{sortBy}</strong>.
                  </H3>
                </NoSurveyContainer>
              )}
            </SurveyList>
            <StyledPagination
              selectedPage={currentPage}
              onSetPage={setCurrentPage}
              totalResults={totalDocuments}
            />
          </SurveysCard>
        )}
      </PageContentWrapper>
      <GetFeaturedLinkModal
        isOpen={featuredLinkOpen}
        onClose={() => setFeaturedLinkOpen(false)}
        title={currentTitle}
        link={`https://${subdomains?.docs?.[0]?.id}.set.fan`}
      />
      <ConfirmFeatureModal
        isOpen={!!surveyToFeature}
        onClose={() => setSurveyToFeature(null)}
        onConfirm={updateFeatured}
        currentTitle={currentTitle}
        newTitle={surveyToFeature?.title}
        link={`${subdomains?.docs?.[0]?.id}.set.fan`}
      />
      <PublishModal
        isOpen={!!surveyToPublish}
        onClose={() => setSurveyToPublish(null)}
        surveyId={surveyToPublish?.id}
        subdomain={subdomains?.docs?.[0]?.id}
        handlePublish={handlePublish}
        handlePublishSubdomain={handlePublishSubdomain}
        featuredSurveyId={subdomains?.docs?.[0]?.data()?.surveyId}
      />
      {!!editAdditionalGroups && (
        <EditAdditionalGroups
          surveyId={editAdditionalGroups}
          open={!!editAdditionalGroups}
          onClose={() => setEditAdditionalGroups("")}
          refreshTypesense={forceReload}
        />
      )}
      <ConfirmLiveEdit
        isOpen={confirmLiveEditOpen}
        onClose={() => {
          setConfirmLiveEditOpen(false);
          setPendingMenuAction(null);
        }}
        onConfirm={async () => {
          if (pendingMenuAction) {
            await onSurveyMenuSelect(
              pendingMenuAction.menuItem,
              pendingMenuAction.survey,
              { bypassConfirm: true },
            );
          }
          setConfirmLiveEditOpen(false);
          setPendingMenuAction(null);
        }}
      />
    </DefaultLayout>
  );
};

const NoSurveyContainer = styled(FlexColumn)`
  align-items: center;
  padding: 20px 53px;
  max-width: 372px;
  margin: 50px auto 50px auto;

  svg {
    width: 118px;
    height: 118px;
    color: #e8e5f1;
    margin-bottom: 16px;
  }

  ${H3} {
    color: #333333;
    font-weight: 400;
    font-size: 18px;
    line-height: 24px;
    text-align: center;
  }
`;

export const BulletList = styled.div<{ $iconColor?: string }>`
  display: grid;
  grid-template-columns: 20px 1fr;
  column-gap: 10px;
  row-gap: 16px;
  color: #333333;
  ${Li} {
    line-height: 20px;
  }
  svg {
    color: ${(p) => p.$iconColor};
    width: 20px;
    height: 20px;
  }

  ${({ theme }) => theme.mediaQueries.mobile} {
    column-gap: 8px;
    row-gap: 12px;
    svg {
      width: 18px;
      height: 18px;
    }
    ${Li} {
      font-size: 12px;
      line-height: 18px;
    }
  }
`;

const CreateSurveyButton = styled(Button)`
  ${({ theme }) => theme.media.mobile} {
    width: auto;
    padding: 10px 16px;
  }
`;

const CardActions = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 31px;

  & > div:nth-child(2) {
    display: flex;
  }

  ${({ theme }) => theme.media.mobile} {
    flex-direction: column;
    width: 100%;
    gap: 20px;

    & > div:nth-child(2) {
      width: 100%;
      justify-content: space-between;
    }
  }
`;

const SearchInput = styled(TextInput)`
  ${({ theme }) => theme.media.desktop} {
    width: 380px;
  }
`;

const SurveyList = styled(FlexColumn)`
  & > div:nth-last-child(n + 2) {
    margin-bottom: 20px;
  }
`;

const SurveysCard = styled(Card)`
  display: flex;
  flex-direction: column;
  position: relative;
  background-color: white;
  box-shadow: 0px 0px 50px rgba(0, 0, 0, 0.15);
  padding: 29px 30px;
  border: none;

  ${({ theme }) => theme.media.mobile} {
    width: 100%;
    padding: 20px;
    box-shadow: 0px 0px 25px rgba(0, 0, 0, 0.15);
  }
`;
